/**
 * Queue Domain - English Translations
 */

export const en = {
  queue: {
    plantStatus: {
      title: 'Trucks at the plant',
      outside: 'Outside the plant',
      inside: 'Inside the plant',
      none: 'No trucks',
      empty: 'No trucks in the queue right now.',
      updated: 'Updated {{time}}',
    },
    waitTimes: {
      title: 'Current waiting time',
      hint: 'Estimated wait if you join the queue now',
      none: 'No wait',
      minutes: '~{{minutes}} min',
      hours: '~{{hours}} h',
      hoursMinutes: '~{{hours}} h {{minutes}} min',
      unknown: 'Not available',
    },
    // Moving a truck in the queue
    reorderTitle: 'Move {{truck}} in the queue',
    reorderDescription: "Changes this truck's place in the line. Its arrival time is not altered.",
    reorderMoveTo: 'Move to',
    reorderFront: 'Front of the queue',
    reorderBack: 'Back of the queue',
    reorderAfter: 'After a specific truck',
    reorderAfterWhich: 'Place it after',
    reorderPickTruck: 'Choose a truck',
    reorderReason: 'Reason',
    reorderReasonPlaceholder: 'Why is this truck going ahead?',
    reorderReasonHint: 'At least 3 characters. Somebody may read this months from now.',
    reorderRecordedNotice: 'Your name, the reason, and the positions moved between are all recorded.',
    reorderConfirm: 'Move truck',
    reorderMoving: 'Moving…',
    reorderDone: 'Truck moved.',
    reorderError: {
      entry_already_called: 'That truck has already been called, so it cannot be moved.',
      reason_required: 'Please give a reason of at least 3 characters.',
      entry_not_in_queue: 'That truck is no longer in this queue.',
      target_not_in_queue: 'The truck you chose is no longer in this queue.',
      target_is_the_entry: 'A truck cannot be placed after itself.',
      no_room_between: 'No room left between those two trucks. Choose a different place.',
      already_there: 'That truck is already in that position.',
      entry_not_found: 'That truck could not be found.',
      unknown: 'The truck could not be moved.',
    },

    // Queue simulation (read-only sandbox over the live yard)
    simBaseline: 'today',
    simOnItsWay: 'on its way',
    simIdle: 'idle',
    simStatWaiting: 'Waiting',
    simStatDone: 'Loaded',
    simStatWorst: 'Worst wait',
    simModeLive: 'The yard right now',
    simModeReplay: 'Replay a past day',
    simModeBlank: 'Build a day',
    simModeLiveHint: 'Start from the trucks actually queued at this moment.',
    simModeReplayHint: 'Real trucks, real arrival times, real loading times from a shift that happened.',
    simModeBlankHint: 'Real bays and settings, no trucks. You choose the orders.',
    simPickADay: 'Pick a day',
    simDayTrucks: '{{trucks}} trucks',
    simNoDays: 'No past days with enough traffic to replay.',
    simPlay: 'Play',
    simPause: 'Pause',
    simStep: 'Next step',
    simAddTrucks: 'Send trucks in',
    simAddN: 'Send {{count}}',
    simStart: 'Start',
    simStarting: 'Loading the yard…',
    simHowItEnded: 'How the day ended',
    simDayComplete: 'Every truck has been loaded.',
    simTheTrade: "Read both rows together. Pooling's gain is the worst case, and it is paid for in the middle.",
    simTitle: 'Queue simulator',
    simSubtitle: 'Try a change against the yard as it stands. Nothing here is saved.',
    simSubtitleFactory: '{{factory}} — the yard as it stands. Nothing here is saved.',
    simReadOnlyNotice: 'This is a sandbox. It reads the real yard and works out what would happen — no bay is closed, no driver is called, and no queue is changed.',
    simBays: 'Bays',
    simNoBays: 'No active bays for this factory.',
    simProductLines: 'Product lines',
    simNoOneWaiting: 'Nobody waiting.',
    simWaitingAnyBay: '{{count}} waiting · any bay that can load it',
    simAlsoTakes: 'also',
    simOpen: 'Open',
    simClosed: 'Closed',
    simNotLoading: 'Not loading',
    simEmptySlot: 'empty',
    simCalledExpires: 'called · {{minutes}}m to show',
    simBoundWaiting: 'Bound & waiting ({{count}})',
    simSendAnOrder: 'Send an order in',
    simSearchOrders: 'Search by order number',
    simNoOrders: 'No orders available to send in.',
    simNoCustomer: 'No customer',
    simTypicalLoad: 'usually {{minutes}}m',
    simSendIn: 'Send in',
    simPlus15: '+15 min',
    simPlusHour: '+1 hour',
    simUndo: 'Undo',
    simResetToLive: 'Back to live',
    simSoFar: 'So far — {{name}}',
    simChangesMade: 'What you changed ({{count}})',
    simWhatHappened: 'What happened',
    simNothingYet: 'Nothing yet. Move the clock forward or send an order in.',
    simWhatThisAssumes: 'What this assumes',
    simWhatChanges: 'What changes',
    simVs: 'vs {{baseline}}',
    simNoMaterialDifference: "No meaningful difference here. Don't read anything into it.",
    simCompareTodayPooled: 'Today vs pooled',
    simCompareTodaySmart: 'Today vs smart bay',
    simCompareAll: 'All three',
    simCompareTodayOnly: 'Today only',
    simMetricServed: 'Loaded and gone',
    simMetricWaiting: 'Still waiting',
    simMetricNoShow: 'Called, never came',
    simMetricMessages: 'Messages sent',
    simMetricMedianWait: 'Median wait',
    simMetricP90Wait: '9 in 10 wait under',
    simMetricWorstWait: 'Worst wait',
    simMetricIdleBay: 'Bay-minutes idle with trucks waiting',
    simMetricOvertakes: 'Overtakes within a product',
    simMetricPreExisting: 'Already loading when you started',
    simWaitExplainer: 'Waiting time counts trucks that were never called as having waited the whole time — otherwise a queue that strands people scores well.',
    // Section Titles
    title: 'Queue Management',
    loadingLocations: 'Loading Locations',
    loadingLocationRules: 'Loading Location Rules',
    loadingLocationRule: 'Loading Location Rule',
    queueEntries: 'Queue Entries',
    dashboard: 'Queue Dashboard',

    // Queue switch (packhouse role)
    queueOpen: 'Queue Open',
    queueClosed: 'Queue Closed',
    bulk: 'Bulk',
    bags: 'Bags',

    // Actions
    addLocation: 'Add Loading Location',
    editLocation: 'Edit Loading Location',
    deleteLocation: 'Delete Loading Location',
    addRule: 'Add Rule',
    editRule: 'Edit Rule',
    deleteRule: 'Delete Rule',
    viewQueue: 'View Queue',
    refreshQueue: 'Refresh Queue',
    joinQueue: 'Join Queue',
    cancelEntry: 'Cancel Entry',
    validateEntry: 'Validate Entry',
    recordExit: 'Record Exit',

    // Location Form
    locationForm: {
      title: 'Loading Location Details',
      subtitle: 'Configure a new loading point for queue management',
      // Sections
      sectionIdentity: 'Identity',
      sectionAssignment: 'Assignment',
      sectionCapacity: 'Capacity & Display',
      // Fields
      label: 'Location Name',
      labelPlaceholder: 'e.g. Bay 1, Silo 3',
      locationKind: 'Location Type',
      selectLocationKind: 'Select type',
      locationNumber: 'Number',
      locationNumberPlaceholder: '1',
      deviceId: 'Device ID',
      deviceIdPlaceholder: 'device-bay-01',
      deviceIdHint: 'Unique identifier for the kiosk / display device',
      factory: 'Factory',
      selectFactory: 'Select factory',
      noFactories: 'No factories available',
      variant: 'Product / Variant',
      selectVariant: 'Select product',
      noVariants: 'No products available',
      allowedVariants: 'Extra Products',
      allowedVariantsHelp: 'Select additional product variants this location can handle',
      primaryVariant: 'Default / Primary Product',
      noPrimary: 'No preference',
      maxVehicles: 'Max Vehicles',
      maxVehiclesPlaceholder: '2',
      maxVehiclesHelp: 'Vehicles allowed inside simultaneously',
      windowNumber: 'Window Number',
      windowNumberPlaceholder: '0',
      monitorIndex: 'Monitor Index',
      monitorIndexPlaceholder: '0',
      isActive: 'Active',
      isActiveHelp: 'Accept new queue entries',
    },

    // Rule Form
    ruleForm: {
      section: 'Rule Configuration',
      loadingLocation: 'Loading Location',
      selectLoadingLocation: 'Select loading location',
      cities: 'Cities',
      customers: 'Customers',
      selectCities: 'Select cities',
      selectCustomers: 'Select customers',
      allCustomers: 'All Customers',
      anyCity: 'Any City',
      priority: 'Priority',
      description: 'Description',
      descriptionPlaceholder: 'Optional description',
      isActive: 'Active',
      isActiveHelp: 'Apply this rule when matching orders to locations',
    },

    // Rule Fields
    ruleLoadingLocation: 'Loading Location',
    ruleCities: 'Cities',
    rulePriority: 'Priority',
    ruleDescription: 'Description',
    ruleFilterLocation: 'Loading Location',
    ruleFilterCity: 'City',

    // Location Kind
    locationKind: {
      bay: 'Bay',
      silo: 'Silo',
    },

    // Queue Status
    status: {
      waiting: 'Waiting',
      ready: 'Ready',
      called: 'Called',
      entered: 'Entered',
      loading: 'Loading',
      completed: 'Completed',
      cancelled: 'Cancelled',
      expired: 'Expired',
      noShow: 'No Show',
      loaded: 'Loaded',
    },

    // Event Types
    eventType: {
      queue_joined: 'Joined Queue',
      status_changed: 'Status Changed',
      notification_sent: 'Notification Sent',
      entry_validated: 'Entry Validated',
      entered_factory: 'Entered Factory',
      entered_loading: 'At Loading Point',
      exited_factory: 'Exited Factory',
      slot_released: 'Slot Released',
      cancelled: 'Cancelled',
    },

    // Statistics
    stats: {
      totalLocations: 'Total Locations',
      activeLocations: 'Active Locations',
      totalCapacity: 'Total Capacity',
      currentOccupancy: 'Current Occupancy',
      totalInQueue: 'In Queue',
      waitingCount: 'Waiting',
      readyCount: 'Ready to Enter',
      insideCount: 'Inside Factory',
      availableSlots: 'Available Slots',
      queueLength: 'Queue Length',
      avgWaitTime: 'Avg Wait Time',
    },

    // Entry Details
    entry: {
      ticketNumber: 'Ticket Number',
      queueNumber: 'Queue Number',
      orderNumber: 'Order Number',
      position: 'Position in Queue',
      driver: 'Driver',
      phone: 'Phone',
      vehicle: 'Vehicle',
      order: 'Order',
      product: 'Product',
      location: 'Loading Location',
      requestedAt: 'Requested At',
      notifiedAt: 'Notified At',
      readySince: 'Ready since',
      enteredAt: 'Entered At',
      exitedAt: 'Exited At',
      waitTime: 'Wait Time',
      totalTime: 'Total Time',
    },

    // Messages
    messages: {
      locationCreated: 'Loading location created successfully',
      locationUpdated: 'Loading location updated successfully',
      locationDeleted: 'Loading location deleted successfully',
      ruleCreated: 'Rule created successfully',
      ruleUpdated: 'Rule updated successfully',
      ruleDeleted: 'Rule deleted successfully',
      joinedQueue: 'Successfully joined queue',
      entryAllowed: 'Entry allowed - proceed to loading location',
      entryDenied: 'Entry denied - please wait',
      exitRecorded: 'Exit recorded successfully',
      entryCancelled: 'Queue entry cancelled',
      queueRefreshed: 'Queue refreshed',
    },

    // Validation
    validation: {
      labelRequired: 'Location name is required',
      locationKindRequired: 'Location type is required',
      locationNumberRequired: 'Location number is required',
      locationNumberInvalid: 'Location number must be positive',
      deviceIdRequired: 'Device ID is required',
      deviceIdUnique: 'Device ID must be unique',
      variantRequired: 'Product is required',
      maxVehiclesRequired: 'Maximum vehicles is required',
      maxVehiclesInvalid: 'Maximum vehicles must be at least 1',
      orderRequired: 'Order is required',
      duplicateEntry: 'Order already has a queue entry',
      invalidToken: 'Invalid entry token',
      notReady: 'Not ready to enter - please wait',
      alreadyEntered: 'Already entered factory',
      notInFactory: 'Vehicle not in factory',
    },

    // Confirmation
    confirm: {
      deleteLocation: 'Are you sure you want to delete this loading location?',
      deleteLocationWarning:
        'This will remove the location from the queue system. Active queue entries will be affected.',
      cancelEntry: 'Are you sure you want to cancel this queue entry?',
      cancelEntryWarning: 'The driver will need to rejoin the queue.',
      deleteRule: 'Are you sure you want to delete this rule?',
      deleteRuleWarning: 'This will remove the rule and the location may be assigned via fallback.',
    },

    // Empty States
    empty: {
      noLocations: 'No loading locations found',
      noLocationsDescription: 'Create a loading location to start managing queues',
      noEntries: 'No queue entries found',
      noEntriesDescription: 'Queue entries will appear when drivers join',
      noEvents: 'No recent events',
      noEventsDescription: 'Queue activity will appear here',
      noRules: 'No rules found',
      noRulesDescription: 'Create a rule to control which loading locations are assigned based on order criteria',
    },

    // Instructions
    instructions: {
      showQrAtGate: 'Show this QR code at the entry gate',
      waitForNotification: 'Wait for notification when ready to enter',
      proceedToLocation: 'Proceed to designated loading location',
      followStaffDirections: 'Follow staff directions at the plant',
    },

    // Tooltips
    tooltips: {
      refreshQueue: 'Re-evaluate queue positions and notify drivers',
      activeStatus: 'Toggle location availability',
      maxVehicles: 'Maximum vehicles that can be at this location simultaneously',
      queuePosition: 'Current position in queue',
    },

    // Error Keys (returned from backend as i18n keys)
    errors: {
      orderNotFound: 'Order not found',
      noResourceAssigned: 'No resource assigned to shipment',
      alreadyInQueue: 'Already in queue for this order',
      cityRequired: 'Please select a city before joining the queue',
      vehiclePlateAlreadyInQueue: 'This vehicle plate already has an active queue entry. Please wait until the previous entry is completed or cancelled.',
      noLoadingLocation: 'No loading location available',
      sapSalesOrderFailed: 'SAP sales order creation failed. Please try again or contact support.',
      insufficientContractBalance: 'Insufficient contract balance. Please contact your account manager.',
      accountRequestInProgress: 'Another request for this account is being processed. Please scan again in a moment.',
      noActiveContract: 'No active contract found. Please contact your account manager.',
      noSapMaterialCode: 'Product configuration error. Please contact your account manager.',
      entryNotFound: 'Queue entry not found',
      cannotCancelTerminal: 'Cannot cancel a completed or expired entry',
      locationNotFound: 'Loading location not found',
      noActiveQueueEntry: 'Access denied — no active queue entry found for this vehicle',
      ticketUsedOrCancelled: 'Ticket has already been used or cancelled',
      ticketExpired: 'Ticket has expired',
      alreadyInside: 'Access denied — vehicle is already inside the facility',
      notReady: 'Slot not yet available — please wait for your notification',
      scanFailed: 'Scan failed — please try again',
      fetchLocationsFailed: 'Failed to fetch loading locations',
      fetchLocationFailed: 'Failed to fetch loading location',
      createLocationFailed: 'Failed to create loading location',
      updateLocationFailed: 'Failed to update loading location',
      deleteLocationFailed: 'Failed to delete loading location',
      fetchEntriesFailed: 'Failed to fetch queue entries',
      fetchEntryFailed: 'Failed to fetch queue entry',
      joinFailed: 'Failed to join queue',
      cancelFailed: 'Failed to cancel entry',
      fetchDashboardFailed: 'Failed to fetch dashboard',
      fetchEventsFailed: 'Failed to fetch events',
      refreshFailed: 'Failed to refresh queue',
      createRuleFailed: 'Failed to create rule',
      updateRuleFailed: 'Failed to update rule',
      deleteRuleFailed: 'Failed to delete rule',
      fetchRulesFailed: 'Failed to fetch rules',
    },

    // Success Keys
    success: {
      welcomeProceed: 'Welcome! Proceed to your assigned bay.',
    },

    // Timeline / Audit Log
    timelineTab: 'Timeline',
    timelineEmpty: 'No activity yet',
    timelineEmptyDesc: 'Changes to this loading location will appear here.',
    historyTab: 'History',
    historyEmpty: 'No events recorded',
    historyEmptyDesc: 'Queue activity for this entry will appear here.',
  },

  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    search: 'Search',
    filter: 'Filter',
    refresh: 'Refresh',
    loading: 'Loading...',
    noResults: 'No results found',
    actions: 'Actions',
    status: 'Status',
    all: 'All',
    active: 'Active',
    inactive: 'Inactive',
    yes: 'Yes',
    no: 'No',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    print: 'Print',
    download: 'Download',
    minutes: 'minutes',
    hours: 'hours',
    ago: 'ago',
  },
}

export type QueueTranslations = typeof en
